import { NgModule, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { NgxMaskDirective, NgxMaskPipe } from 'ngx-mask';
import { ReactiveFormsModule } from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSliderModule } from '@angular/material/slider';
import { FormsModule } from '@angular/forms';
import { LightgalleryModule } from 'lightgallery/angular';
import { TippyDirective } from '@ngneat/helipopper';
import { OverlayModule } from '@angular/cdk/overlay';

import { NgIconsModule } from '@ng-icons/core';
import {
  featherTrendingUp,
  featherUser,
  featherPackage,
  featherFileText,
  featherChevronDown,
  featherChevronRight,
  featherXCircle,
  featherSquare,
  featherCheckSquare,
  featherCheckCircle,
  featherUploadCloud,
  featherGrid,
  featherDollarSign,
  featherHome,
  featherEdit,
  featherMoreVertical,
  featherShare,
  featherArrowRight,
  featherMonitor,
  featherSmartphone,
  featherShoppingBag,
  featherExternalLink,
  featherSearch,
  featherHelpCircle,
  featherArrowLeftCircle,
  featherArrowRightCircle,
  featherChevronUp,
  featherAlertTriangle,
  featherPenTool,
  featherMessageSquare,
  featherBell,
} from '@ng-icons/feather-icons';

import { InputComponent } from './components/input/input.component';
import { ButtonComponent } from './components/button/button.component';
import { IconComponent } from './components/icon/icon.component';
import { PropertyListComponent } from './components/property-list/property-list.component';
import { PropertyListInfoComponent } from './components/property-list/info/info.component';
import { PropertyListStatusComponent } from './components/property-list/status/status.component';
import { PropertyListLabelComponent } from './components/property-list/label/label.component';
import { PropertyShareCopyLinkComponent } from './components/property-share-modal/copy-link/copy-link.component';
import { PropertyShareButtonComponent } from './components/property-share-button/property-share-button.component';
import { PropertyShareModalComponent } from './components/property-share-modal/property-share-modal.component';
import { PropertyShareEmailsComponent } from './components/property-share-modal/share-emails/share-emails.component';
import { PropertyShareSMSComponent } from './components/property-share-modal/share-sms/share-sms.component';
import { PropertyShareBuyersListComponent } from './components/property-share-modal/share-buyers-list/share-buyers-list.component';
import { FavoritePropertyComponent } from './components/favorite-property/favorite-property.component';
import { ContainerComponent } from './components/container/container.component';
import { PageTitleComponent } from './components/page-title/page-title.component';
import { ModalComponent } from './components/modal/modal.component';
import { PaginatorComponent } from './components/paginator/paginator.component';
import { LoadingComponent } from './components/loading/loading.component';
import { CarouselComponent } from './components/carousel/carousel.component';
import { OfferModalComponent } from './components/offer-modal/offer-modal.component';
import { NoResultsComponent } from './components/no-results/no-results.component';
import { ProfileLayoutComponent } from './components/profile-layout/profile-layout.component';
import { ProfileSidebarComponent } from './components/profile-layout/sidebar/sidebar.component';
import { MultiSelectorComponent } from './components/multi-selector/multi-selector.component';

import { TopbarComponent } from './components/topbar/topbar.component';
import { MenuMobileComponent } from './components/topbar/menu-mobile/menu-mobile.component';
import { UserComponent } from './components/topbar/user/user.component';
import { FooterComponent } from './components/footer/footer.component';

import { FiltersV2Component } from './components/filters-v2/filters-v2.component';
import { FilterV2ButtonsComponent } from './components/filters-v2/filter-v2-buttons/filter-v2-buttons.component';
import { FilterV2StatusComponent } from './components/filters-v2/filter-v2-status/filter-v2-status.component';
import { FilterV2PriceComponent } from './components/filters-v2/filter-v2-price/filter-v2-price.component';
import { FilterV2SubtypeComponent } from './components/filters-v2/filter-v2-subtype/filter-v2-subtype.component';
import { FilterV2MoreComponent } from './components/filters-v2/filter-v2-more/filter-v2-more.component';
import { FilterV2MoreDesktopComponent } from './components/filters-v2/filter-v2-more-desktop/filter-v2-more-desktop.component';
import { FilterV2BedsComponent } from './components/filters-v2/filter-v2-beds/filter-v2-beds.component';
import { FilterV2BathsComponent } from './components/filters-v2/filter-v2-baths/filter-v2-baths.component';
import { FilterV2SqftComponent } from './components/filters-v2/filter-v2-sqft/filter-v2-sqft.component';
import { FilterV2LotSizeComponent } from './components/filters-v2/filter-v2-lot-size/filter-v2-lot-size.component';
import { FilterV2YearBuiltComponent } from './components/filters-v2/filter-v2-year-built/filter-v2-year-built.component';
import { FilterV2FinancialsComponent } from './components/filters-v2/filter-v2-financials/filter-v2-financials.component';
import { FilterV2CbsasComponent } from './components/filters-v2/filter-v2-cbsas/filter-v2-cbsas.component';

import { HtmlFormatPipe, StatusPipe, StringPipe, PageUrlPipe } from './pipes';

import { AuthModalComponent } from './components/auth-modal/auth-modal.component';
import { AuthModalHeaderComponent } from './components/auth-modal/header/header.component';

import { SignInComponent } from './components/auth-modal/signin/signin.component';
import { SignInFormComponent } from './components/auth-modal/signin/form/form.component';
import { SignInButtonsComponent } from './components/auth-modal/signin/buttons/buttons.component';
import { SignUpComponent } from './components/auth-modal/signup/signup.component';
import { SignUpFormComponent } from './components/auth-modal/signup/form/form.component';
import { SignUpNoPasswordComponent } from './components/auth-modal/signup/no-password/no-password.component';
import { AddPhoneNumberComponent } from './components/auth-modal/add-phone-number/add-phone-number.component';
import { VerifyIdentityComponent } from './components/auth-modal/verify-identity/verify-identity.component';
import { VerifyIdentityFormComponent } from './components/auth-modal/verify-identity/form/form.component';
import { VerifyIdentityPhoneNumberComponent } from './components/auth-modal/verify-identity/phone-number/phone-number.component';
// import { VerifyIdentityDigitsFormComponent } from './components/auth-modal/verify-identity/digits-form/digits-form.component';
// import { VerifyIdentityPhoneNumberDigitsComponent } from './components/auth-modal/verify-identity/phone-number-digits/phone-number-digits.component';
import { VerifyIdentityUpdatePhoneComponent } from './components/auth-modal/verify-identity/update-phone/update-phone.component';
import { VerifyIdentityEmailConfirmationComponent } from './components/auth-modal/verify-identity/email-confirmation/email-confirmation.component';
import { VerifyIdentityPhoneNumberConfirmationComponent } from './components/auth-modal/verify-identity/phone-number-confirmation/phone-number-confirmation.component';
import { VerifyIdentityEmailUpdateComponent } from './components/auth-modal/verify-identity/email-confirmation/email-update/email-update.component';
import { AccountSetupComponent } from './components/auth-modal/account-setup/account-setup.component';
import { AccountSetupFormComponent } from './components/auth-modal/account-setup/form/form.component';
import { AccountSetupInvestorComponent } from './components/auth-modal/account-setup/form/investor/investor.component';
import { AccountSetupInvestor2Component } from './components/auth-modal/account-setup/form/investor2/investor2.component';
import { AccountSetupMarketsComponent } from './components/auth-modal/account-setup/form/markets/markets.component';
import { WelcomeUserComponent } from './components/auth-modal/welcome-user/welcome-user.component';
import { ReviewCbsaModalComponent } from './components/auth-modal/review-cbsa/review-cbsa.component';

// Home related
import { HomeMobileComponent } from '@modules/home/pages/home-map/home-mobile/home-mobile.component';
import { MapViewComponent as HomeMapViewComponent } from '@modules/home/pages/home-map/map-view/map-view.component';
import { ListViewComponent as HomeListViewComponent } from '@modules/home/pages/home-map/list-view/list-view.component';
import { HeaderComponent as HomeHeaderComponent } from '@modules/home/components/header/header.component';
import { NoticeComponent as HomeNoticeComponent } from '@modules/home/components/notice/notice.component';
import { HomeMapComponent } from '@modules/home/pages/home-map/home-map.component';
import { SwitcherComponent as HomeSwitcherComponent } from '@modules/home/pages/home-map/switcher/switcher.component';
import { HomeWelcomeGuestComponent } from '@modules/home/components/welcome-guest/welcome-guest.component';

import { ForgotPasswordComponent } from './components/auth-modal/forgot-password/forgot-password.component';
import { ForgotPasswordFormComponent } from './components/auth-modal/forgot-password/form/form.component';
import { ResetPasswordSentComponent } from './components/auth-modal/reset-password-sent/reset-password-sent.component';
import { ResetPasswordComponent } from './components/auth-modal/reset-password/reset-password.component';
import { ResetPasswordFormComponent } from './components/auth-modal/reset-password/form/form.component';
import { SearchMultiSelectorComponent } from './components/search-multi-selector/search-multi-selector.component';
import { SubmitADealComponent } from './components/submit-a-deal/submit-a-deal.component';
import { BreadcrumbComponent } from './components/breadcrumb/breadcrumb.component';
import { OrganizationShareComponent } from './components/organization-share/organization-share.component';
import { OrganizationShareCopyLinkComponent } from './components/organization-share/copy-link/copy-link.component';
import { OrganizationTopbarComponent } from './components/organization-topbar/topbar.component';
import { IframeModalComponent } from './components/iframe-modal/iframe-modal.component';
import { OrganizationShareEmailComponent } from './components/organization-share/share-email/share-email.component';
import { OrganizationShareSmsComponent } from './components/organization-share/share-sms/share-sms.component';
import { OrganizationShareBuyersListComponent } from './components/organization-share/share-buyers-list/share-buyers-list.component';
import { TitleHelpComponent } from './components/title-help/title-help.component';
import { PromoteModalComponent } from './components/promote-modal/promote-modal.component';
import { HelpMsgButtonComponent } from './components/help-msg-button/help-msg-button.component';
import { UserNotificationsBellComponent } from './components/user-notifications-bell/user-notifications-bell.component';

@NgModule({
  declarations: [
    InputComponent,
    ButtonComponent,
    IconComponent,
    PropertyListComponent,
    PropertyListInfoComponent,
    PropertyListStatusComponent,
    PropertyListLabelComponent,
    PropertyShareButtonComponent,
    PropertyShareCopyLinkComponent,
    PropertyShareModalComponent,
    PropertyShareEmailsComponent,
    PropertyShareSMSComponent,
    PropertyShareBuyersListComponent,
    FavoritePropertyComponent,
    ContainerComponent,
    PageTitleComponent,
    ModalComponent,
    PaginatorComponent,
    LoadingComponent,
    OfferModalComponent,
    NoResultsComponent,
    CarouselComponent,
    TopbarComponent,
    MenuMobileComponent,
    UserComponent,
    FooterComponent,
    ProfileLayoutComponent,
    ProfileSidebarComponent,
    HtmlFormatPipe,
    StatusPipe,
    StringPipe,
    PageUrlPipe,
    MultiSelectorComponent,
    FiltersV2Component,
    FilterV2ButtonsComponent,
    FilterV2StatusComponent,
    FilterV2PriceComponent,
    FilterV2SubtypeComponent,
    FilterV2MoreComponent,
    FilterV2MoreDesktopComponent,
    FilterV2BedsComponent,
    FilterV2BathsComponent,
    FilterV2SqftComponent,
    FilterV2LotSizeComponent,
    FilterV2YearBuiltComponent,
    FilterV2FinancialsComponent,
    FilterV2CbsasComponent,

    AuthModalComponent,
    AuthModalHeaderComponent,
    SignInComponent,
    SignInFormComponent,
    SignInButtonsComponent,
    SignUpComponent,
    SignUpFormComponent,
    SignUpNoPasswordComponent,
    AddPhoneNumberComponent,
    VerifyIdentityComponent,
    VerifyIdentityFormComponent,
    VerifyIdentityPhoneNumberComponent,
    // VerifyIdentityDigitsFormComponent,
    // VerifyIdentityPhoneNumberDigitsComponent,
    VerifyIdentityUpdatePhoneComponent,
    VerifyIdentityEmailConfirmationComponent,
    VerifyIdentityPhoneNumberConfirmationComponent,
    VerifyIdentityEmailUpdateComponent,
    AccountSetupComponent,
    AccountSetupFormComponent,
    AccountSetupInvestorComponent,
    AccountSetupInvestor2Component,
    AccountSetupMarketsComponent,
    WelcomeUserComponent,
    ReviewCbsaModalComponent,
    ForgotPasswordComponent,
    ForgotPasswordFormComponent,
    ResetPasswordSentComponent,
    ResetPasswordComponent,
    ResetPasswordFormComponent,
    SearchMultiSelectorComponent,
    SubmitADealComponent,
    BreadcrumbComponent,

    // Home related
    HomeMobileComponent,
    HomeMapViewComponent,
    HomeListViewComponent,
    HomeHeaderComponent,
    HomeNoticeComponent,
    HomeMapComponent,
    HomeSwitcherComponent,
    HomeWelcomeGuestComponent,

    OrganizationShareComponent,
    OrganizationShareCopyLinkComponent,
    OrganizationTopbarComponent,
    IframeModalComponent,
    OrganizationShareEmailComponent,
    OrganizationShareSmsComponent,

    OrganizationShareBuyersListComponent,
    TitleHelpComponent,
    PromoteModalComponent,
    HelpMsgButtonComponent,
    UserNotificationsBellComponent,
  ],
  imports: [
    CommonModule,
    MatMenuModule,
    RouterModule,
    MatProgressBarModule,
    NgxMaskDirective,
    NgxMaskPipe,
    ReactiveFormsModule,
    MatSelectModule,
    MatCheckboxModule,
    MatSliderModule,
    FormsModule,
    LightgalleryModule,
    TippyDirective,
    NgIconsModule.withIcons({
      featherTrendingUp,
      featherUser,
      featherPackage,
      featherFileText,
      featherChevronRight,
      featherChevronDown,
      featherChevronUp,
      featherXCircle,
      featherSquare,
      featherCheckSquare,
      featherUploadCloud,
      featherCheckCircle,
      featherGrid,
      featherDollarSign,
      featherHome,
      featherEdit,
      featherMoreVertical,
      featherShare,
      featherArrowRight,
      featherMonitor,
      featherSmartphone,
      featherShoppingBag,
      featherExternalLink,
      featherSearch,
      featherHelpCircle,
      featherArrowLeftCircle,
      featherArrowRightCircle,
      featherAlertTriangle,
      featherPenTool,
      featherMessageSquare,
      featherBell,
    }),
    OverlayModule,
  ],
  exports: [
    InputComponent,
    ButtonComponent,
    IconComponent,
    PropertyListComponent,
    PropertyListStatusComponent,
    FavoritePropertyComponent,
    ProfileLayoutComponent,
    ContainerComponent,
    PageTitleComponent,
    ModalComponent,
    PaginatorComponent,
    LoadingComponent,
    OfferModalComponent,
    NoResultsComponent,
    CarouselComponent,
    MultiSelectorComponent,
    TopbarComponent,
    FooterComponent,
    FiltersV2Component,
    HtmlFormatPipe,
    StatusPipe,
    StringPipe,
    AuthModalComponent,
    PromoteModalComponent,
    PropertyShareModalComponent,
    AccountSetupInvestorComponent,
    AccountSetupInvestor2Component,
    AccountSetupMarketsComponent,
    BreadcrumbComponent,
    SubmitADealComponent,
    NgIconsModule,
    IframeModalComponent,

    OrganizationShareComponent,
    OrganizationTopbarComponent,
    ProfileSidebarComponent,

    // Home related
    HomeMobileComponent,
    HomeMapViewComponent,
    HomeListViewComponent,
    HomeHeaderComponent,
    HomeNoticeComponent,
    HomeMapComponent,
    HomeSwitcherComponent,
    HomeWelcomeGuestComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class SharedModule {}
